import { createTheme } from "@mui/material/styles";

/**
 * KSENA Stavby MUI theme — Google Apps design language.
 *
 * Follows Google Workspace visual style: Google Sans / Roboto fonts,
 * Google color palette, pill-shaped buttons, clean surfaces.
 */

const googleFontFamily =
  '"Google Sans", "Roboto", "Helvetica Neue", "Arial", sans-serif';

/**
 * Mobile font breakpoint — bump base font sizes on phones because the
 * construction workforce uses phones on site and needs larger text.
 *
 * 768px is the long-standing "phone or small tablet" cutoff and it lines
 * up with our `xs`/`sm` MUI breakpoint divide.
 */
const MOBILE_BREAKPOINT = "@media (max-width: 768px)";

const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#1a73e8",
      light: "#4285f4",
      dark: "#1765cc",
      contrastText: "#fff",
    },
    secondary: {
      main: "#5f6368",
      light: "#80868b",
      dark: "#3c4043",
      contrastText: "#fff",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    error: {
      main: "#d93025",
    },
    warning: {
      main: "#f9ab00",
    },
    success: {
      main: "#1e8e3e",
    },
    info: {
      main: "#1a73e8",
    },
    text: {
      primary: "#202124",
      // Bumped from Google's #5f6368 (4.6:1) to #4a4d51 (~6.4:1) so secondary
      // captions on cards/forms stay readable on the lighter card surfaces.
      secondary: "#4a4d51",
    },
    divider: "#dadce0",
    action: {
      hover: "rgba(26, 115, 232, 0.04)",
      selected: "rgba(26, 115, 232, 0.08)",
      focus: "rgba(26, 115, 232, 0.12)",
    },
  },
  typography: {
    fontFamily: googleFontFamily,
    h1: { fontSize: "2rem", fontWeight: 400, letterSpacing: 0 },
    h2: { fontSize: "1.75rem", fontWeight: 400, letterSpacing: 0 },
    h3: { fontSize: "1.5rem", fontWeight: 500, letterSpacing: 0 },
    h4: { fontSize: "1.25rem", fontWeight: 500, letterSpacing: "0.0075em" },
    h5: { fontSize: "1.1rem", fontWeight: 500, letterSpacing: 0 },
    h6: { fontSize: "1rem", fontWeight: 500, letterSpacing: "0.0075em" },
    subtitle1: { fontSize: "0.938rem", fontWeight: 500, letterSpacing: "0.009em" },
    subtitle2: { fontSize: "0.813rem", fontWeight: 500, letterSpacing: "0.007em" },
    body1: { fontSize: "0.875rem", letterSpacing: "0.01em", lineHeight: 1.5 },
    body2: { fontSize: "0.813rem", letterSpacing: "0.01em", lineHeight: 1.43 },
    button: { textTransform: "none", fontWeight: 500, letterSpacing: "0.025em" },
    caption: { fontSize: "0.75rem", letterSpacing: "0.025em", color: "#4a4d51" },
    overline: { fontSize: "0.688rem", fontWeight: 500, letterSpacing: "0.08em" },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: "0.875rem",
          [MOBILE_BREAKPOINT]: {
            fontSize: "1rem",
            lineHeight: 1.55,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 20,
          padding: "8px 24px",
          fontWeight: 500,
          fontSize: "0.875rem",
          [MOBILE_BREAKPOINT]: {
            fontSize: "1rem",
            minHeight: 44,
            padding: "8px 20px",
          },
        },
        containedPrimary: {
          "&:hover": { backgroundColor: "#1765cc" },
        },
        outlined: {
          borderColor: "#dadce0",
          color: "#1a73e8",
          "&:hover": {
            backgroundColor: "rgba(26, 115, 232, 0.04)",
            borderColor: "#d2e3fc",
          },
        },
        text: {
          color: "#1a73e8",
          "&:hover": {
            backgroundColor: "rgba(26, 115, 232, 0.04)",
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          textTransform: "none",
          boxShadow: "0 1px 3px 0 rgba(60,64,67,0.3), 0 4px 8px 3px rgba(60,64,67,0.15)",
          "&:hover": {
            boxShadow: "0 2px 3px 0 rgba(60,64,67,0.3), 0 6px 10px 4px rgba(60,64,67,0.15)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: "1px solid #dadce0",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: "0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)",
        },
        elevation2: {
          boxShadow: "0 1px 2px 0 rgba(60,64,67,0.3), 0 2px 6px 2px rgba(60,64,67,0.15)",
        },
        elevation3: {
          boxShadow: "0 1px 3px 0 rgba(60,64,67,0.3), 0 4px 8px 3px rgba(60,64,67,0.15)",
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: "outlined", size: "small" },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#dadce0",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#202124",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#1a73e8",
            borderWidth: 2,
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            fontWeight: 500,
            fontSize: "0.75rem",
            color: "#5f6368",
            backgroundColor: "#f8f9fa",
            letterSpacing: "0.025em",
            textTransform: "uppercase",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid #e8eaed",
          padding: "12px 16px",
          fontSize: "0.875rem",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 16,
          fontSize: "0.75rem",
        },
        outlined: {
          borderColor: "#dadce0",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: "none",
          backgroundColor: "#f8f9fa",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "inset 0 -1px 0 0 #dadce0",
          backgroundColor: "#ffffff",
          color: "#202124",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          "&.Mui-selected": {
            backgroundColor: "#e8f0fe",
            color: "#1a73e8",
            "&:hover": {
              backgroundColor: "#d2e3fc",
            },
            "& .MuiListItemIcon-root": {
              color: "#1a73e8",
            },
          },
          "&:hover": {
            backgroundColor: "#f1f3f4",
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: "#5f6368",
          minWidth: 40,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: "0 1px 3px 0 rgba(60,64,67,0.3), 0 4px 8px 3px rgba(60,64,67,0.15)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1.125rem",
          fontWeight: 500,
          padding: "24px 24px 16px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.875rem",
          minWidth: "auto",
          letterSpacing: "0.01em",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "0.75rem",
          backgroundColor: "#202124",
          borderRadius: 4,
          padding: "4px 8px",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#5f6368",
          "&:hover": {
            backgroundColor: "rgba(95, 99, 104, 0.08)",
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: "#1a73e8",
          },
          "&.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "#1a73e8",
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: "0 1px 2px 0 rgba(60,64,67,0.3), 0 2px 6px 2px rgba(60,64,67,0.15)",
          border: "1px solid #dadce0",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          minHeight: 36,
          "&:hover": {
            backgroundColor: "#f1f3f4",
          },
          "&.Mui-selected": {
            backgroundColor: "#e8f0fe",
            "&:hover": {
              backgroundColor: "#d2e3fc",
            },
          },
        },
      },
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#8ab4f8",
      light: "#aecbfa",
      dark: "#669df6",
      contrastText: "#202124",
    },
    secondary: {
      main: "#9aa0a6",
      light: "#bdc1c6",
      dark: "#80868b",
      contrastText: "#202124",
    },
    background: {
      default: "#1f1f1f",
      paper: "#292929",
    },
    error: {
      main: "#f28b82",
    },
    warning: {
      main: "#fdd663",
    },
    success: {
      main: "#81c995",
    },
    info: {
      main: "#8ab4f8",
    },
    text: {
      primary: "#e8eaed",
      secondary: "#9aa0a6",
    },
    divider: "#3c4043",
    action: {
      hover: "rgba(138, 180, 248, 0.08)",
      selected: "rgba(138, 180, 248, 0.16)",
      focus: "rgba(138, 180, 248, 0.12)",
    },
  },
  typography: {
    fontFamily: googleFontFamily,
    h1: { fontSize: "2rem", fontWeight: 400, letterSpacing: 0 },
    h2: { fontSize: "1.75rem", fontWeight: 400, letterSpacing: 0 },
    h3: { fontSize: "1.5rem", fontWeight: 500, letterSpacing: 0 },
    h4: { fontSize: "1.25rem", fontWeight: 500, letterSpacing: "0.0075em" },
    h5: { fontSize: "1.1rem", fontWeight: 500, letterSpacing: 0 },
    h6: { fontSize: "1rem", fontWeight: 500, letterSpacing: "0.0075em" },
    subtitle1: { fontSize: "0.938rem", fontWeight: 500, letterSpacing: "0.009em" },
    subtitle2: { fontSize: "0.813rem", fontWeight: 500, letterSpacing: "0.007em" },
    body1: { fontSize: "0.875rem", letterSpacing: "0.01em", lineHeight: 1.5 },
    body2: { fontSize: "0.813rem", letterSpacing: "0.01em", lineHeight: 1.43 },
    button: { textTransform: "none", fontWeight: 500, letterSpacing: "0.025em" },
    caption: { fontSize: "0.75rem", letterSpacing: "0.025em", color: "#9aa0a6" },
    overline: { fontSize: "0.688rem", fontWeight: 500, letterSpacing: "0.08em" },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: "0.875rem",
          [MOBILE_BREAKPOINT]: {
            fontSize: "1rem",
            lineHeight: 1.55,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 20,
          padding: "8px 24px",
          fontWeight: 500,
          fontSize: "0.875rem",
          "&.Mui-disabled": {
            color: "#c4c7c5",
            borderColor: "#5f6368",
          },
          "&.MuiButton-contained.Mui-disabled": {
            backgroundColor: "#3c4043",
            color: "#c4c7c5",
          },
          [MOBILE_BREAKPOINT]: {
            fontSize: "1rem",
            minHeight: 44,
            padding: "8px 20px",
          },
        },
        containedPrimary: {
          backgroundColor: "#8ab4f8",
          color: "#202124",
          "&:hover": { backgroundColor: "#aecbfa" },
        },
        outlined: {
          borderColor: "#5f6368",
          color: "#8ab4f8",
          "&:hover": {
            backgroundColor: "rgba(138, 180, 248, 0.08)",
            borderColor: "#8ab4f8",
          },
        },
        text: {
          color: "#8ab4f8",
          "&:hover": {
            backgroundColor: "rgba(138, 180, 248, 0.08)",
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          textTransform: "none",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.5), 0 4px 8px 3px rgba(0,0,0,0.3)",
          "&:hover": {
            boxShadow: "0 2px 3px 0 rgba(0,0,0,0.5), 0 6px 10px 4px rgba(0,0,0,0.3)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: "1px solid #3c4043",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 1px 2px 0 rgba(0,0,0,0.5), 0 1px 3px 1px rgba(0,0,0,0.3)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: "0 1px 2px 0 rgba(0,0,0,0.5), 0 1px 3px 1px rgba(0,0,0,0.3)",
        },
        elevation2: {
          boxShadow: "0 1px 2px 0 rgba(0,0,0,0.5), 0 2px 6px 2px rgba(0,0,0,0.3)",
        },
        elevation3: {
          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.5), 0 4px 8px 3px rgba(0,0,0,0.3)",
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: "outlined", size: "small" },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "#c4c7c5",
          "&.Mui-focused": {
            color: "#8ab4f8",
          },
          "&.Mui-disabled": {
            color: "#9aa0a6",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          color: "#f1f3f4",
          backgroundColor: "#303134",
          "& input::placeholder, & textarea::placeholder": {
            color: "#c4c7c5",
            opacity: 1,
          },
          "& .MuiSvgIcon-root": {
            color: "#c4c7c5",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#5f6368",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#e8eaed",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#8ab4f8",
            borderWidth: 2,
          },
          "&.Mui-disabled": {
            color: "#c4c7c5",
            backgroundColor: "#303134",
          },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: "#5f6368",
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            fontWeight: 500,
            fontSize: "0.75rem",
            color: "#9aa0a6",
            backgroundColor: "#292929",
            letterSpacing: "0.025em",
            textTransform: "uppercase",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid #3c4043",
          padding: "12px 16px",
          fontSize: "0.875rem",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 16,
          fontSize: "0.75rem",
        },
        outlined: {
          borderColor: "#5f6368",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: "none",
          backgroundColor: "#292929",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "inset 0 -1px 0 0 #3c4043",
          backgroundColor: "#292929",
          color: "#e8eaed",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          "&.Mui-selected": {
            backgroundColor: "rgba(138, 180, 248, 0.16)",
            color: "#8ab4f8",
            "&:hover": {
              backgroundColor: "rgba(138, 180, 248, 0.24)",
            },
            "& .MuiListItemIcon-root": {
              color: "#8ab4f8",
            },
          },
          "&:hover": {
            backgroundColor: "rgba(232, 234, 237, 0.08)",
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: "#9aa0a6",
          minWidth: 40,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          backgroundColor: "#303134",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.5), 0 4px 8px 3px rgba(0,0,0,0.3)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1.125rem",
          fontWeight: 500,
          padding: "24px 24px 16px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.875rem",
          minWidth: "auto",
          letterSpacing: "0.01em",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "0.75rem",
          backgroundColor: "#e8eaed",
          color: "#202124",
          borderRadius: 4,
          padding: "4px 8px",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#9aa0a6",
          "&:hover": {
            backgroundColor: "rgba(232, 234, 237, 0.08)",
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: "#8ab4f8",
          },
          "&.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "#8ab4f8",
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: "0 1px 2px 0 rgba(0,0,0,0.5), 0 2px 6px 2px rgba(0,0,0,0.3)",
          border: "1px solid #3c4043",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          minHeight: 36,
          "&:hover": {
            backgroundColor: "rgba(232, 234, 237, 0.08)",
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(138, 180, 248, 0.16)",
            "&:hover": {
              backgroundColor: "rgba(138, 180, 248, 0.24)",
            },
          },
        },
      },
    },
  },
});

export { lightTheme, darkTheme };
export default lightTheme;
